import { cadOpType } from "../factory";
import { Parameter3dOP } from "../solids/parameter-3d";
import { copyIPoint } from "../../math/point";
import { IPoint } from "../../math/types";
import { vectorDist2D } from "../../math/distance";
import { createBufferPPrism, createPPrism } from "../../geometries/solid/pprism";
import { PPrismCommand } from "../../commands/primitives/pprism";
import { settingsOpModes } from "../step-operations";
import { getCurrentSolidMaterial } from "lib/materials";
import { addEulerAnglesToEulerAngles } from "lib/math/plane";

export class PPrismOP extends Parameter3dOP {
  public opType = cadOpType.PPRISM;
  protected static sampleData = { radius: 1, height: 2, sides: 5 };

  constructor() {
    super();
    this.offSet.x = 0;
    this.offSet.z = PPrismOP.sampleData.height * 0.5;
  }
  protected iniSettingsOp() {
    this.settingsOpManager.setCfg([{
      infoMsg: "Insert center or [Sides]: ",
      stepMode: settingsOpModes.DEFAULTXYZ,
      cmdLineListener: (cmd: string) => {
        if (cmd === "s") {
          this.setNextStep(3);
        } else {
          this.addPointFromExt(cmd);
        }
      },
      endStepCallback: () => {
        const planeManager = this.graphicProcessor.getPlaneManager();
        planeManager.activePlane.position = this.basePoint;
        planeManager.activePlane.locked = true;
      }
    }, {
      infoMsg: "Insert radius: ",
      stepMode: settingsOpModes.ONEBOX,
      currValue: () => (PPrismOP.sampleData.radius).toFixed(3),
      cmdLineListener: (cmd: string) => {
        const r = parseFloat(cmd);
        if (r) {
          PPrismOP.sampleData.radius = r;
          this.setNextStep();
          this.numPoints++;
          this.recalculateObj();
        }
      },
      endStepCallback: () => {
        const planeManager = this.graphicProcessor.getPlaneManager();
        planeManager.activePlane.rotation = addEulerAnglesToEulerAngles({ x: Math.PI * 0.5, y: 0, z: 0 }, this.rotation);
      }
    }, {
      infoMsg: "Insert height: ",
      stepMode: settingsOpModes.ONEBOX,
      currValue: () => (PPrismOP.sampleData.height).toFixed(3),
      cmdLineListener: (cmd: string) => {
        const h = parseFloat(cmd);
        if (h) {
          PPrismOP.sampleData.height = h;
          this.offSet.z = PPrismOP.sampleData.height * 0.5;
          this.recalculateObj();
          this.endOperation();
        }
      },
      endStepCallback: this.endOperation.bind(this),
    }, {
      infoMsg: "Insert sides: ",
      stepMode: settingsOpModes.ONEBOX,
      currValue: () => (PPrismOP.sampleData.sides).toFixed(3),
      cmdLineListener: (cmd: string) => {
        PPrismOP.sampleData.sides = parseInt(cmd);
        this.recalculateObj();
        this.setNextStep(0);
      },
    }]);
  }
  protected createAux() {
    return createPPrism(
      PPrismOP.sampleData.radius,
      PPrismOP.sampleData.height,
      PPrismOP.sampleData.sides,
      false
    );
  }
  protected createGeom() {
    return createBufferPPrism(
      PPrismOP.sampleData.radius,
      PPrismOP.sampleData.height,
      PPrismOP.sampleData.sides,
      false
    );
  }

  public moveLastPoint(point: IPoint) {
    if (this.numPoints === 0) {
      this.basePoint = copyIPoint(point);
      const planeManager = this.graphicProcessor.getPlaneManager();
      this.rotation = planeManager.activePlane.rotation;
      this.recalculateObj();

    } else if (this.numPoints === 1) {
      const planeManager = this.graphicProcessor.getPlaneManager();
      const p1 = planeManager.activePlane.getRelativePoint(point);
      PPrismOP.sampleData.radius = vectorDist2D({ x: 0, y: 0, z: 0 }, p1);
      this.recalculateObj();

    } else if (this.numPoints === 2) {
      const planeManager = this.graphicProcessor.getPlaneManager();
      const p1 = planeManager.activePlane.getRelativePoint(point);
      PPrismOP.sampleData.height = p1.y;
      this.offSet.z = PPrismOP.sampleData.height * 0.5;
      this.recalculateObj();
    }
  }

  public save(): void {
    if (this.graphicProcessor) {
      const command = new PPrismCommand(
        PPrismOP.sampleData.radius,
        PPrismOP.sampleData.height,
        PPrismOP.sampleData.sides,
        false,
        this.basePoint,
        this.rotation,
        this.offSet,
        { x: 1, y: 1, z: 1 },
        this.getCurrentSceneId(),
        this.graphicProcessor,
        getCurrentSolidMaterial()
      );
      if (command) this.graphicProcessor.storeAndExecute(command);
    }
  }
}
